'use client'
// =============================================================================
// TiCouneTab — la QUESTION à Ti-Coune, à part. L'historique d'un document (versions, journal) reste sur la page de l'Éditeur ;
// la question se pose ici, dans le panneau IA, sur le document dont l'« Historique et questions » est ouvert (page-context.store).
// Lecture seule : Ti-Coune lit la version courante et répond ; il ne modifie rien (les modifications passent par une proposition
// à valider, dans l'Éditeur).
// =============================================================================

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePageContext } from '../../../../stores/page-context.store'

const mono = 'var(--font-mono)'
const label: React.CSSProperties = { fontSize: 9, letterSpacing: '0.12em', color: 'var(--text-muted)', marginBottom: 6, fontFamily: mono }
const btn: React.CSSProperties = { fontSize: 10, fontFamily: mono, padding: '5px 11px', borderRadius: 6, color: '#93c5fd', border: '1px solid rgba(147,197,253,0.35)', background: 'transparent', cursor: 'pointer', textDecoration: 'none' }

export function TiCouneTab() {
  const doc = usePageContext(s => s.document)
  const [q, setQ] = useState('')
  const [asking, setAsking] = useState(false)
  const [answer, setAnswer] = useState<{ text?: string; note?: string; truncated?: boolean } | null>(null)
  useEffect(() => { setAnswer(null); setQ('') }, [doc?.fileId])   // un autre document : on repart d'une page blanche

  if (!doc) {
    return (
      <div style={{ padding: 14, fontSize: 11, lineHeight: 1.7, color: 'var(--text-muted)' }}>
        <div style={label}>QUESTION À TI-COUNE</div>
        Aucun document n’est ouvert. Ouvrez « Historique et questions » d’un document dans l’Éditeur : la question portera sur lui.
        <div style={{ marginTop: 10 }}><Link href="/bureau/fichiers?tab=documents" style={btn}>Ouvrir l’Éditeur</Link></div>
      </div>
    )
  }

  async function ask() {
    if (!doc || !q.trim() || asking) return
    setAsking(true); setAnswer(null)
    try {
      const res = await fetch('/api/wopi/analyze', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileId: doc.fileId, question: q }) })
      const b = await res.json().catch(() => ({}))
      if (res.ok && b.mode === 'llm') setAnswer({ text: b.text, truncated: b.truncated })
      else setAnswer({ note: `Ti-Coune n’a pas pu répondre. Cause : ${b.code ?? b.error ?? `HTTP_${res.status}`}${b.detail ? ` — ${b.detail}` : ''}.` })
    } catch { setAnswer({ note: 'Ti-Coune n’a pas pu répondre. Cause : serveur injoignable.' }) }
    setAsking(false); doc.refresh()
  }

  return (
    <div style={{ padding: 14, display: 'grid', gap: 10 }}>
      <div>
        <div style={label}>QUESTION À TI-COUNE (lecture seule)</div>
        <div style={{ fontSize: 11, color: 'var(--text-primary)', wordBreak: 'break-word' }}>Sur : <strong>{doc.name}</strong></div>
      </div>
      <textarea value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask() } }} maxLength={1000} rows={3}
        placeholder="ex. Résume ce document ; quelles sont les échéances ?" aria-label="Question à Ti-Coune sur ce document"
        style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 11, fontFamily: mono, outline: 'none', resize: 'vertical' }} />
      <div><button onClick={ask} disabled={asking || !q.trim()} style={{ ...btn, opacity: asking || !q.trim() ? 0.5 : 1 }}>{asking ? 'Lecture…' : 'Demander'}</button></div>
      {answer?.text && (
        <div style={{ fontSize: 11, lineHeight: 1.6, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
          {answer.text}
          <div style={{ marginTop: 6, fontSize: 9, color: 'var(--text-muted)' }}>
            Ti-Coune a lu le texte de la version courante{answer.truncated ? ' (tronqué : seul le début a été lu)' : ''}. Il n’a rien modifié ; c’est à vous d’appliquer une suggestion dans l’éditeur.
          </div>
        </div>
      )}
      {answer?.note && <div style={{ fontSize: 10, color: '#fcd34d' }}>{answer.note}</div>}
    </div>
  )
}
